import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  LuMessagesSquare,
  LuArrowLeft,
  LuTicketCheck,
  LuCreditCard,
  LuCircleAlert,
  LuShieldCheck,
  LuRefreshCw,
} from "react-icons/lu";

import EventHelpTooltip from "../components/EventPreview/EventHelpTooltip";
import { ReactComponent as TribeLogo } from "../assets/TribeLogo.svg";
import { ReactComponent as VisaSvg } from "../assets/Payment-Logos/Visa.svg";
import { ReactComponent as MastercardSvg } from "../assets/Payment-Logos/Mastercard.svg";
import { ReactComponent as AmexSvg } from "../assets/Payment-Logos/Amex.svg";
import { ReactComponent as DiscoverSvg } from "../assets/Payment-Logos/Discover-card-light.svg";
import MembershipIllustration from "../assets/Illustrations/MembershipIllustration.svg";
import ActiveMembershipIllustration from "../assets/Illustrations/Enter-Password-1--Streamline-Milano (1).svg";
import MinimalFooter from "../components/MinimalFooter";

/* ---------- Header ---------- */

const headerContainerStyle = {
  display: "flex",
  flexDirection: "column",
  position: "sticky",
  top: 0,
  background: "#fff",
  zIndex: 100,
  fontFamily:
    "'Inter', system-ui, -apple-system, Segoe UI, Roboto, Helvetica, Arial, sans-serif",
};

const headerLogoRowStyle = {
  display: "flex",
  justifyContent: "center",
  paddingTop: "0.5rem",
};

const logoSvgStyle = {
  width: 72,
  height: "auto",
};

const headerNavRowStyle = {
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  padding: "0.5rem 1rem",
  position: "relative",
  height: 44,
};

const previousBackButtonStyle = {
  position: "absolute",
  left: 16,
  background: "#000",
  color: "#fff",
  borderRadius: 999,
  width: 40,
  height: 28,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  cursor: "pointer",
  border: "none",
};

const pageTitlePlainStyle = {
  fontSize: "0.95rem",
  fontWeight: 900,
  lineHeight: 1,
  textTransform: "uppercase",
  letterSpacing: 0.2,
};

/**
 * TEMPORARY PREVIEW
 *
 * true  = show every state underneath each other
 * false = show the real membership state only
 */
const SHOW_ALL_STATES = false;

const PREVIEW_STATES = [
  {
    label: "1. Good standing",
    membership: {
      membershipActive: true,
      membershipStatus: "active",
      reservationAccessStatus: "active",
      canReserve: true,

      hasCardOnFile: false,
      membershipCardRequired: false,

      membershipReservationLimit: 3,
      membershipReservationsUsed: 0,
      membershipReservationsRemaining: 3,
      membershipReservationRefillsAt: "2026-08-14T12:00:00.000Z",

      membershipNoShowStrikes: 0,
      reservationAttendanceStreak: 0,
      attendanceWarning: false,

      membershipNoShowFeeAmount: 10,
      membershipLateCancelFeeAmount: 10,
      membershipLateCancelHours: 6,
    },
  },

  {
    label: "2. Attendance warning · 0 of 2 attended",
    membership: {
      membershipActive: true,
      membershipStatus: "active",
      reservationAccessStatus: "warning",
      canReserve: true,

      hasCardOnFile: false,
      membershipCardRequired: false,

      membershipReservationLimit: 3,
      membershipReservationsUsed: 0,
      membershipReservationsRemaining: 3,
      membershipReservationRefillsAt: "2026-08-14T12:00:00.000Z",

      membershipNoShowStrikes: 1,
      reservationAttendanceStreak: 0,
      attendanceWarning: true,
      attendancesUntilReset: 2,

      membershipNoShowFeeAmount: 10,
      membershipLateCancelFeeAmount: 10,
      membershipLateCancelHours: 6,
    },
  },

  {
    label: "3. Attendance warning · 1 of 2 attended",
    membership: {
      membershipActive: true,
      membershipStatus: "active",
      reservationAccessStatus: "warning",
      canReserve: true,

      hasCardOnFile: false,
      membershipCardRequired: false,

      membershipReservationLimit: 3,
      membershipReservationsUsed: 1,
      membershipReservationsRemaining: 2,
      membershipReservationRefillsAt: "2026-08-14T12:00:00.000Z",

      membershipNoShowStrikes: 1,
      reservationAttendanceStreak: 1,
      attendanceWarning: true,
      attendancesUntilReset: 1,

      membershipNoShowFeeAmount: 10,
      membershipLateCancelFeeAmount: 10,
      membershipLateCancelHours: 6,
    },
  },

  {
    label: "4. Reservation limit reached",
    membership: {
      membershipActive: true,
      membershipStatus: "active",
      reservationAccessStatus: "limit_reached",
      canReserve: false,

      hasCardOnFile: false,
      membershipCardRequired: false,

      membershipReservationLimit: 3,
      membershipReservationsUsed: 3,
      membershipReservationsRemaining: 0,
      membershipReservationRefillsAt: "2026-08-14T12:00:00.000Z",

      membershipNoShowStrikes: 0,
      reservationAttendanceStreak: 0,
      attendanceWarning: false,

      membershipNoShowFeeAmount: 10,
      membershipLateCancelFeeAmount: 10,
      membershipLateCancelHours: 6,
    },
  },

  {
    label: "5. Card permanently required",
    membership: {
      membershipActive: false,
      membershipStatus: "card_required",
      reservationAccessStatus: "card_required",
      canReserve: false,

      hasCardOnFile: false,
      membershipCardRequired: true,

      membershipReservationLimit: 3,
      membershipReservationsUsed: 0,
      membershipReservationsRemaining: 3,
      membershipReservationRefillsAt: "2026-08-14T12:00:00.000Z",

      membershipNoShowStrikes: 2,
      reservationAttendanceStreak: 0,
      attendanceWarning: false,

      membershipNoShowFeeAmount: 10,
      membershipLateCancelFeeAmount: 10,
      membershipLateCancelHours: 6,
    },
  },

  {
    label: "6. Required card has an issue",
    membership: {
      membershipActive: false,
      membershipStatus: "card_issue",
      reservationAccessStatus: "card_issue",
      canReserve: false,

      hasCardOnFile: true,
      membershipPaymentMethodId: "pm_preview",
      membershipCardBrand: "visa",
      membershipCardLast4: "4242",

      membershipCardRequired: true,
      membershipCardRequiresReplacement: true,

      membershipReservationLimit: 3,
      membershipReservationsUsed: 0,
      membershipReservationsRemaining: 3,
      membershipReservationRefillsAt: "2026-08-14T12:00:00.000Z",

      membershipNoShowStrikes: 2,
      reservationAttendanceStreak: 0,
      attendanceWarning: false,

      membershipNoShowFeeAmount: 10,
      membershipLateCancelFeeAmount: 10,
      membershipLateCancelHours: 6,
    },
  },

  {
    label: "7. Required card saved",
    membership: {
      membershipActive: true,
      membershipStatus: "active",
      reservationAccessStatus: "active",
      canReserve: true,

      hasCardOnFile: true,
      membershipPaymentMethodId: "pm_preview",
      membershipCardBrand: "visa",
      membershipCardLast4: "4242",

      membershipCardRequired: true,
      membershipCardRequiresReplacement: false,

      membershipReservationLimit: 3,
      membershipReservationsUsed: 1,
      membershipReservationsRemaining: 2,
      membershipReservationRefillsAt: "2026-08-14T12:00:00.000Z",

      membershipNoShowStrikes: 2,
      reservationAttendanceStreak: 0,
      attendanceWarning: false,

      membershipNoShowFeeAmount: 10,
      membershipLateCancelFeeAmount: 10,
      membershipLateCancelHours: 6,
    },
  },
];

export default function SubscriptionPage({
  previewMembership = null,
  embeddedPreview = false,
}) {
  const navigate = useNavigate();

  const [membership, setMembership] = useState(previewMembership);
  const [loading, setLoading] = useState(!previewMembership);

  useEffect(() => {
    if (previewMembership) {
      setMembership(previewMembership);
      setLoading(false);
      return;
    }

    async function fetchMembership() {
      try {
        const token = localStorage.getItem("accessToken");

        const res = await fetch(
          `${process.env.REACT_APP_API_URL}/subscriptions/me`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );

        const data = await res.json();
        setMembership(data);
      } catch (err) {
        console.error("membership fetch failed:", err);
      } finally {
        setLoading(false);
      }
    }

    fetchMembership();
  }, [previewMembership]);

  const status = String(
    membership?.membershipStatus || "inactive"
  ).toLowerCase();

  const reservationAccessStatus = String(
    membership?.reservationAccessStatus || ""
  ).toLowerCase();

  const cardBrand = String(
    membership?.membershipCardBrand || ""
  ).trim();

  const cardLast4 = String(
    membership?.membershipCardLast4 || ""
  ).trim();

  const hasCardOnFile = Boolean(
    membership?.hasCardOnFile ||
      membership?.membershipPaymentMethodId ||
      cardLast4
  );

  const membershipCardRequired =
    membership?.membershipCardRequired === true;

  const isCardIssue =
    reservationAccessStatus === "card_issue" ||
    membership?.membershipCardRequiresReplacement === true ||
    status === "card_issue" ||
    status === "past_due" ||
    status === "unpaid";

  const isAttendanceWarning =
    reservationAccessStatus === "warning" ||
    membership?.attendanceWarning === true ||
    (
      Number(membership?.membershipNoShowStrikes || 0) === 1 &&
      !membershipCardRequired
    );

  const used = Number(
    membership?.membershipReservationsUsed || 0
  );

  const limit = Number(
    membership?.membershipReservationLimit || 3
  );

  const left = Number.isFinite(
    Number(membership?.membershipReservationsRemaining)
  )
    ? Math.max(
        0,
        Number(membership.membershipReservationsRemaining)
      )
    : Math.max(0, limit - used);

  const accessStatus =
    reservationAccessStatus ||
    (isCardIssue
      ? "card_issue"
      : membershipCardRequired && !hasCardOnFile
      ? "card_required"
      : isAttendanceWarning
      ? "warning"
      : left <= 0
      ? "limit_reached"
      : "active");

  const isCardRequired =
    accessStatus === "card_required";

  const isLimitReached =
    accessStatus === "limit_reached";

  const isActive =
    accessStatus === "active";

    const isOptionalCardDisabled =
  !membershipCardRequired &&
  !hasCardOnFile &&
  (isActive || isAttendanceWarning);


  const attendanceStreak = Math.max(
    0,
    Math.min(
      2,
      Number(
        membership?.reservationAttendanceStreak || 0
      )
    )
  );

  const attendancesRemaining = Math.max(
    0,
    2 - attendanceStreak
  );

  const refillDate = formatRefillDate(
    membership?.membershipReservationRefillsAt
  );

  const noShowFee = Number(
    membership?.membershipNoShowFeeAmount ?? 10
  );

  const lateCancelFee = Number(
    membership?.membershipLateCancelFeeAmount ??
      noShowFee
  );

  const lateCancelHours = Number(
    membership?.membershipLateCancelHours ?? 6
  );

  const lateCancelLabel = `${lateCancelHours} hour${
    lateCancelHours === 1 ? "" : "s"
  }`;

  const feePolicy =
    noShowFee === lateCancelFee
      ? `Future no-shows or cancellations within ${lateCancelLabel} cost $${formatMoney(
          noShowFee
        )}.`
      : `Future no-shows cost $${formatMoney(
          noShowFee
        )}. Cancellations within ${lateCancelLabel} cost $${formatMoney(
          lateCancelFee
        )}.`;

  const heroTitle = (() => {
    if (isCardIssue) {
      return "Update your card";
    }

    if (isCardRequired) {
      return "Add a card to continue";
    }

    if (isAttendanceWarning) {
      return "Attendance warning";
    }

    if (isLimitReached) {
      return "Reservation limit reached";
    }

    return "Ready to reserve";
  })();

  const heroSubtitle = (() => {
    if (isCardIssue) {
      return "Your saved card needs attention. Update it to restore reservation access.";
    }

    if (isCardRequired) {
      return "After repeated no-shows, a card is now required for future reservations.";
    }

    if (isAttendanceWarning) {
      return attendancesRemaining === 1
        ? "Attend your next gathering to return your account to good standing."
        : "Attend your next two gatherings in a row to return your account to good standing.";
    }

    if (isLimitReached) {
      return `You’ve used all ${limit} reservations for this refill period.`;
    }

    return membershipCardRequired
      ? "Your reservation access is active and your required card is saved."
      : "You can reserve gatherings without putting a card on file.";
  })();

  const circleBg = (() => {
    if (isCardIssue || isCardRequired) {
      return "#FEE2E2";
    }

    if (isAttendanceWarning) {
      return "#FEF3C7";
    }

    if (isLimitReached) {
      return "#F3F4F6";
    }

    return "#DDF7E7";
  })();

  const handleStartMembership = () => {
    navigate("/membership/purchase", {
      state: {
        purchaseType: "membership",
        event: {
          _id: "membership",
          name: "Tribe Membership",
          price: 0,
          eventDate: "",
          startTime12: "",
          endTime12: "",
          address: "Add a reservation card",
        },
      },
    });
  };

  const handleUpdatePayment = (reason = "change") => {
    navigate("/membership/purchase", {
      state: {
        purchaseType: "membershipUpdatePayment",
        updatePaymentReason: reason,
        failedPaymentMethodId:
          reason === "failed"
            ? membership?.membershipPaymentMethodId || ""
            : "",
        defaultPaymentMethodId:
          membership?.membershipPaymentMethodId || "",
        event: {
          _id: "membership-update-payment",
          name: "Update Card",
          price: 0,
          eventDate: "",
          startTime12: "",
          endTime12: "",
          address: "Update your reservation card",
        },
      },
    });
  };

  const handleCardAction = () => {
    if (isCardIssue) {
      handleUpdatePayment("failed");
      return;
    }

    if (hasCardOnFile) {
      handleUpdatePayment("change");
      return;
    }

    handleStartMembership();
  };

  if (loading) {
    return (
      <>
        <header style={headerContainerStyle}>
          <div style={headerLogoRowStyle}>
            <TribeLogo style={logoSvgStyle} />
          </div>

          <div style={headerNavRowStyle}>
            <button
              type="button"
              onClick={() => navigate(-1)}
              style={previousBackButtonStyle}
              aria-label="Go back"
            >
              <LuArrowLeft size={16} color="#fff" />
            </button>

            <div style={pageTitlePlainStyle}>
              RESERVATION SETTINGS
            </div>

            <div
              style={{
                position: "absolute",
                right: 16,
              }}
            >
              <EventHelpTooltip
                title="Reservation access"
                body="A card is not required unless repeated no-shows make one necessary for future reservations."
              />
            </div>
          </div>
        </header>

        <div style={styles.page}>
          Loading reservation settings...
        </div>
      </>
    );
  }

  if (SHOW_ALL_STATES && !embeddedPreview) {
    return (
      <>
        <header style={headerContainerStyle}>
          <div style={headerLogoRowStyle}>
            <TribeLogo style={logoSvgStyle} />
          </div>

          <div style={headerNavRowStyle}>
            <button
              type="button"
              onClick={() => navigate(-1)}
              style={previousBackButtonStyle}
              aria-label="Go back"
            >
              <LuArrowLeft size={16} color="#fff" />
            </button>

            <div style={pageTitlePlainStyle}>
              Reservation Settings
            </div>

            <div
              style={{
                position: "absolute",
                right: 16,
              }}
            >
              <EventHelpTooltip
                title="Reservation access"
                body="A card is not required unless repeated no-shows make one necessary for future reservations."
              />
            </div>
          </div>
        </header>

        <div style={styles.previewPage}>
          <div style={styles.previewWrap}>
            {PREVIEW_STATES.map((preview) => (
              <div
                key={preview.label}
                style={styles.previewState}
              >
                <div style={styles.previewLabel}>
                  {preview.label}
                </div>

                <SubscriptionPage
                  previewMembership={preview.membership}
                  embeddedPreview
                />
              </div>
            ))}
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      {!embeddedPreview && (
        <header style={headerContainerStyle}>
          <div style={headerLogoRowStyle}>
            <TribeLogo style={logoSvgStyle} />
          </div>

          <div style={headerNavRowStyle}>
            <button
              type="button"
              onClick={() => navigate(-1)}
              style={previousBackButtonStyle}
              aria-label="Go back"
            >
              <LuArrowLeft size={16} color="#fff" />
            </button>

            <div style={pageTitlePlainStyle}>
              Reservation Settings
            </div>

            <div
              style={{
                position: "absolute",
                right: 16,
              }}
            >
              <EventHelpTooltip
                title="Reservation access"
                body="A card is not required unless repeated no-shows make one necessary for future reservations."
              />
            </div>
          </div>
        </header>
      )}

      <div
        style={
          embeddedPreview
            ? styles.embeddedPreviewPage
            : styles.page
        }
      >
        <section style={styles.hero}>
          <div style={styles.centerBlock}>
            <div
              style={{
                ...styles.illustrationCircle,
                background: circleBg,
                animation: isActive
                  ? "membershipCirclePulse 2s infinite"
                  : "none",
              }}
            >
              <img
               src={
  isActive || isAttendanceWarning
    ? ActiveMembershipIllustration
    : MembershipIllustration
}
                alt=""
                aria-hidden="true"
                style={{
                  ...styles.heroIllustration,
                  filter: isActive
                    ? "brightness(0) saturate(100%) invert(29%) sepia(38%) saturate(879%) hue-rotate(91deg) brightness(91%) contrast(92%)"
                    : isCardIssue || isCardRequired
                    ? "brightness(0) saturate(100%) invert(18%) sepia(89%) saturate(2406%) hue-rotate(345deg) brightness(89%) contrast(96%)"
                    : isAttendanceWarning
                    ? "brightness(0) saturate(100%) invert(47%) sepia(84%) saturate(922%) hue-rotate(359deg) brightness(94%) contrast(92%)"
                    : "brightness(0) saturate(100%) invert(34%) sepia(7%) saturate(620%) hue-rotate(182deg) brightness(93%) contrast(86%)",
                }}
              />
            </div>

            <div style={styles.activeTitleRow}>
              <h1
                style={
                  isActive
                    ? styles.activeTitle
                    : styles.title
                }
              >
                {heroTitle}
              </h1>
            </div>

            <p style={styles.subtitle}>
              {heroSubtitle}
            </p>

            <div style={styles.card}>
              <div style={styles.membershipTopBlock}>
                <div style={styles.cardLine}>
                  <div style={styles.cardLineLeft}>
                    <div
                      style={{
                        ...styles.cardIconWrap,
                        ...(isCardIssue || isCardRequired
                          ? styles.cardIconWrapIssue
                          : {}),
                        ...(isActive
                          ? styles.cardIconWrapActive
                          : {}),
                      }}
                    >
                      {isCardIssue ? (
                        <LuCircleAlert
                          size={18}
                          strokeWidth={2.5}
                        />
                      ) : (
                        <LuCreditCard
                          size={18}
                          strokeWidth={2}
                        />
                      )}
                    </div>

                    <div>
                      <div style={styles.cardLineTitle}>
                        {isCardIssue
                          ? "Card needs attention"
                          : hasCardOnFile
                          ? "Card on file"
                          : membershipCardRequired
                          ? "Card required"
                          : "Card on file"}
                      </div>

                      <div style={styles.cardLineText}>
                        {hasCardOnFile ? (
                          <PaymentCardMini
                            brand={cardBrand}
                            last4={cardLast4}
                            failed={isCardIssue}
                          />
                        ) : membershipCardRequired ? (
                          "Add a card to restore reservation access."
                        ) : (
                          "Card can’t be added right now"
                        )}
                      </div>
                    </div>
                  </div>

                <button
  type="button"
  disabled={isOptionalCardDisabled}
  style={{
    ...styles.cardActionBtn,
    ...(isCardIssue || isCardRequired
      ? styles.cardActionBtnIssue
      : {}),
    ...(isOptionalCardDisabled
      ? styles.cardActionBtnDisabled
      : {}),
  }}
  onClick={
    isOptionalCardDisabled
      ? undefined
      : handleCardAction
  }
>
  {hasCardOnFile
    ? "Update Card"
    : "Add Card"}
</button>
                </div>

                <div style={styles.policyNote}>
                  {membershipCardRequired
                    ? feePolicy
                    : "A card is not required unless repeated no-shows make it necessary for future reservations."}
                </div>
              </div>

              <div style={styles.divider} />
            </div>
          </div>

          {isCardIssue ? (
            <div style={styles.benefitsSection}>
              <div style={styles.sectionEyebrow}>
                Needs attention
              </div>

              <BenefitItem
                icon={
                  <LuCircleAlert
                    size={23}
                    strokeWidth={2.25}
                  />
                }
                title="Reservation access paused"
                text="Update your saved card to restore access."
              />

              <BenefitItem
                icon={
                  <LuCreditCard
                    size={22}
                    strokeWidth={2.25}
                  />
                }
                title="Saved card"
                text={
                  cardLast4
                    ? `•••• ${cardLast4} needs attention.`
                    : "Your saved payment method needs attention."
                }
              />

              <BenefitItem
                icon={
                  <LuShieldCheck
                    size={23}
                    strokeWidth={2.25}
                  />
                }
                title="Future reservation policy"
                text={feePolicy}
              />
            </div>
          ) : isCardRequired ? (
            <div style={styles.benefitsSection}>
              <div style={styles.sectionEyebrow}>
                Access paused
              </div>

              <BenefitItem
                icon={
                  <LuCreditCard
                    size={22}
                    strokeWidth={2.25}
                  />
                }
                title="Card now required"
                text="After repeated no-shows, a card is permanently required for future reservations."
              />

              <BenefitItem
                icon={
                  <LuTicketCheck
                    size={22}
                    strokeWidth={2.25}
                  />
                }
                title="Access restored after setup"
                text="Add a card and you’ll be able to reserve again immediately."
              />

              <BenefitItem
                icon={
                  <LuShieldCheck
                    size={23}
                    strokeWidth={2.25}
                  />
                }
                title="Future reservation policy"
                text={feePolicy}
              />
            </div>
          ) : isAttendanceWarning ? (
            <div style={styles.benefitsSection}>
              <div style={styles.sectionEyebrow}>
                Good standing
              </div>

              <BenefitItem
                icon={
                  <LuRefreshCw
                    size={23}
                    strokeWidth={2.25}
                  />
                }
                title={
                  attendancesRemaining === 1
                    ? "Attend one more gathering"
                    : "Attend your next two gatherings"
                }
                text={`${attendanceStreak} of 2 attended toward clearing this warning.`}
              />

              <BenefitItem
                icon={
                  <LuTicketCheck
                    size={22}
                    strokeWidth={2.25}
                  />
                }
                title="Reservations remain available"
                text={`You can still reserve normally. ${left} of ${limit} reservations remain this period.`}
              />

              <BenefitItem
                icon={
                  <LuCircleAlert
                    size={23}
                    strokeWidth={2.25}
                  />
                }
                title="Another no-show"
                text="Another no-show before this warning is cleared will permanently require a card for future reservations."
              />
            </div>
          ) : isLimitReached ? (
            <div style={styles.benefitsSection}>
              <div style={styles.sectionEyebrow}>
                Reservations
              </div>

              <BenefitItem
                icon={
                  <LuRefreshCw
                    size={23}
                    strokeWidth={2.25}
                  />
                }
                title="Spots refill"
                text={refillDate}
              />

              <BenefitItem
                icon={
                  <LuTicketCheck
                    size={23}
                    strokeWidth={2.25}
                  />
                }
                title="Limit reached"
                text={`${used} of ${limit} reservations used. New reservations become available when your spots refill.`}
              />

              <BenefitItem
                icon={
                  <LuCreditCard
                    size={22}
                    strokeWidth={2.25}
                  />
                }
                title={
                  membershipCardRequired
                    ? "Required card saved"
                    : "No card required"
                }
                text={
                  membershipCardRequired
                    ? "Your required card remains saved while you wait for reservations to refill."
                    : "You can continue reserving without a card when new spots become available."
                }
              />
            </div>
          ) : (
            <div style={styles.benefitsSection}>
              <div style={styles.sectionEyebrow}>
                Reservations
              </div>

              <BenefitItem
                icon={
                  <LuRefreshCw
                    size={23}
                    strokeWidth={2.25}
                  />
                }
                title="Spots refill"
                text={refillDate}
              />

              <BenefitItem
                icon={
                  <LuTicketCheck
                    size={23}
                    strokeWidth={2.25}
                  />
                }
                title="Used this period"
                text={`${used} of ${limit} reservations used. ${left} left.`}
              />

              <BenefitItem
                icon={
                  <LuShieldCheck
                    size={23}
                    strokeWidth={2.25}
                  />
                }
                title={
                  membershipCardRequired
                    ? "Required card saved"
                    : "No card required"
                }
                text={
                  membershipCardRequired
                    ? feePolicy
                    : "Reserve normally without a card. Repeated no-shows can make a card permanently required for future reservations."
                }
              />

              <BenefitItem
                icon={
                  <LuMessagesSquare
                    size={23}
                    strokeWidth={2.25}
                  />
                }
                title="Platform access"
                text="Match, chat, and stay connected through Tribe’s social features."
              />
            </div>
          )}
        </section>

        {!embeddedPreview && (
          <div style={styles.minimalFooterWrap}>
            <MinimalFooter />
          </div>
        )}
      </div>

      <style>
        {`
          @keyframes membershipCirclePulse {
            0% {
              box-shadow: 0 0 0 0 rgba(34, 197, 94, 0.28);
              transform: scale(1);
            }

            70% {
              box-shadow: 0 0 0 14px rgba(34, 197, 94, 0);
              transform: scale(1.015);
            }

            100% {
              box-shadow: 0 0 0 0 rgba(34, 197, 94, 0);
              transform: scale(1);
            }
          }
        `}
      </style>
    </>
  );
}

function formatMoney(value) {
  const parsed = Number(value);

  if (!Number.isFinite(parsed)) {
    return "0";
  }

  return Number.isInteger(parsed)
    ? String(parsed)
    : parsed.toFixed(2);
}

function formatRefillDate(value) {
  if (!value) {
    return "Not available";
  }

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) {
    return "Not available";
  }

  return date.toLocaleDateString("en-US", {
    month: "long",
    day: "numeric",
    year: "numeric",
  });
}

function BenefitItem({
  icon,
  title,
  text,
}) {
  return (
    <div style={styles.benefitItem}>
      <div style={styles.benefitIcon}>
        {icon}
      </div>

      <div>
        <div style={styles.benefitTitle}>
          {title}
        </div>

        <div style={styles.benefitText}>
          {text}
        </div>
      </div>
    </div>
  );
}

function PaymentCardMini({
  brand,
  last4,
  failed = false,
}) {
  const normalizedBrand = String(
    brand || ""
  )
    .toLowerCase()
    .trim();

  const safeLast4 = String(
    last4 || ""
  ).trim();

  if (!safeLast4) {
    return (
      <span style={styles.paymentNoCard}>
        No card on file
      </span>
    );
  }

  return (
    <span
      style={{
        ...styles.paymentMiniInline,
        color: failed
          ? "#B91C1C"
          : "#555",
      }}
    >
      <span
        style={{
          ...styles.paymentLogoBox,
          ...getBrandBg(normalizedBrand),
          ...(failed
            ? styles.paymentLogoBoxFailed
            : {}),
        }}
      >
        <CardBrandLogo
          brand={normalizedBrand}
        />
      </span>

      <span style={styles.paymentLast4}>
        •••• {safeLast4}
      </span>
    </span>
  );
}

function CardBrandLogo({ brand }) {
  if (brand === "visa") {
    return (
      <VisaSvg
        style={styles.paymentCardSvg}
      />
    );
  }

  if (brand === "mastercard") {
    return (
      <MastercardSvg
        style={styles.paymentCardSvg}
      />
    );
  }

  if (brand === "amex") {
    return (
      <AmexSvg
        style={styles.paymentCardSvg}
      />
    );
  }

  if (brand === "discover") {
    return (
      <DiscoverSvg
        style={styles.paymentCardSvg}
      />
    );
  }

  return (
    <LuCreditCard size={15} />
  );
}

function getBrandBg(brand) {
  return {
    backgroundColor:
      brand === "mastercard"
        ? "#252525"
        : brand === "amex"
        ? "#2F6DC8"
        : "#FFFFFF",
  };
}

const styles = {
  previewPage: {
    width: "100%",
    background: "#fff",
    padding: "18px 20px 50px",
    boxSizing: "border-box",
  },

  previewWrap: {
    width: "100%",
    maxWidth: 620,
    margin: "0 auto",
    display: "grid",
    gap: 46,
  },

  previewState: {
    width: "100%",
    minWidth: 0,
  },

  previewLabel: {
    margin: "0 0 14px 2px",
    color: "#777",
    fontSize: 11,
    lineHeight: 1,
    fontWeight: 850,
    letterSpacing: "0.055em",
    textTransform: "uppercase",
    fontFamily:
      "'Inter', system-ui, -apple-system, Segoe UI, Roboto, Helvetica, Arial, sans-serif",
  },

  embeddedPreviewPage: {
    width: "100%",
    minHeight: 0,
    background: "#fff",
    padding: 0,
    display: "block",
    boxSizing: "border-box",
  },

  page: {
    minHeight: "calc(100vh - 104px)",
    background: "#fff",
    padding: "0px 20px 0",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    boxSizing: "border-box",
  },

  hero: {
    width: "100%",
    maxWidth: "620px",
    textAlign: "left",
  },

  centerBlock: {
    textAlign: "center",
  },

  illustrationCircle: {
    width: 75,
    height: 75,
    borderRadius: "50%",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    margin: "8px auto 18px",
    overflow: "hidden",
  },

  heroIllustration: {
    width: "30px",
    height: "auto",
    display: "block",
  },

  activeTitleRow: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    margin: "0 auto 10px",
  },

  activeTitle: {
    fontSize: "1.5rem",
    lineHeight: 1.04,
    margin: 0,
    color: "#111",
    letterSpacing: "-0.045em",
    fontWeight: 850,
  },

  title: {
    fontSize: "1.5rem",
    lineHeight: 1.04,
    margin: "0 auto 0px",
    color: "#111",
    letterSpacing: "-0.045em",
    maxWidth: "520px",
    fontWeight: 850,
  },

  subtitle: {
    fontSize: "0.85rem",
    lineHeight: 1.2,
    color: "#666",
    margin: "0 auto 28px",
    maxWidth: "500px",
    fontWeight: 500,
  },

  card: {
    background: "#fff",
    borderRadius: "12px",
    padding: "0",
    textAlign: "center",
    border: "0px solid rgba(0,0,0,0.06)",
  },

  membershipTopBlock: {
    maxWidth: 430,
    margin: "0 auto",
    textAlign: "left",
  },

  reservationValue: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: 14,
    marginBottom: 18,
  },

  reservationNumber: {
    fontSize: "4rem",
    lineHeight: 0.85,
    fontWeight: 900,
    color: "#111",
    letterSpacing: "-0.08em",
  },

  reservationCopy: {
    paddingTop: 2,
  },

  reservationLabel: {
    color: "#111",
    fontSize: "1rem",
    lineHeight: 1.06,
    fontWeight: 850,
    letterSpacing: "-0.035em",
  },

  reservationSubtext: {
    marginTop: 6,
    color: "#777",
    fontSize: "0.78rem",
    lineHeight: 1.25,
    fontWeight: 600,
    maxWidth: 190,
  },

  thinRule: {
    height: "1px",
    background: "#eeeeee",
    margin: "0 0 16px",
  },

  cardLine: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 14,
  },

  cardLineLeft: {
    display: "flex",
    alignItems: "center",
    gap: 10,
    minWidth: 0,
  },

  cardIconWrap: {
    width: 36,
    height: 36,
    borderRadius: "50%",
    background: "#F3F4F6",
    color: "#111",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
  },

  cardIconWrapActive: {
    background: "#eee",
    color: "#000",
  },

  cardIconWrapIssue: {
    background: "#FEF2F2",
    color: "#B91C1C",
  },

  cardLineTitle: {
    fontSize: "0.9rem",
    lineHeight: 1.1,
    fontWeight: 850,
    color: "#111",
    letterSpacing: "-0.025em",
  },

  cardLineText: {
    marginTop: 4,
    fontSize: "0.78rem",
    lineHeight: 1.2,
    color: "#777",
    fontWeight: 600,
  },

  cardActionBtn: {
    height: 36,
    borderRadius: 999,
    border: "1px solid #111",
    background: "#111",
    color: "#fff",
    fontSize: 11,
    fontWeight: 850,
    padding: "0 14px",
    cursor: "pointer",
    whiteSpace: "nowrap",
    flexShrink: 0,
  },

  cardActionBtnIssue: {
    background: "#B91C1C",
    borderColor: "#B91C1C",
  },

  cardActionBtnDisabled: {
  background: "#E5E7EB",
  borderColor: "#E5E7EB",
  color: "#9CA3AF",
  cursor: "not-allowed",
},

  policyNote: {
    margin: "14px auto 0",
    paddingTop: 14,
    borderTop: "1px solid #eeeeee",
    color: "#777",
    fontSize: "0.77rem",
    lineHeight: 1.35,
    fontWeight: 650,
    textAlign: "center",
  },

  divider: {
    height: "1px",
    background: "#eee",
    margin: "20px 0 22px",
  },

  benefitsSection: {
    display: "grid",
    gap: "22px",
  },

  sectionEyebrow: {
    color: "#555",
    fontSize: "0.88rem",
    lineHeight: 1,
    fontWeight: 850,
    letterSpacing: "0.08em",
    textTransform: "uppercase",
    marginBottom: "-2px",
  },

  benefitItem: {
    display: "grid",
    gridTemplateColumns: "40px 1fr",
    gap: "8px",
    alignItems: "flex-start",
  },

  benefitIcon: {
    width: "30px",
    height: "30px",
    color: "#111",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    marginTop: "1px",
    flexShrink: 0,
  },

  benefitTitle: {
    color: "#111",
    fontSize: "0.9rem",
    lineHeight: 1.18,
    fontWeight: 850,
    letterSpacing: "-0.028em",
  },

  benefitText: {
    marginTop: "4px",
    color: "#777",
    fontSize: "0.88rem",
    lineHeight: 1.34,
    fontWeight: 500,
  },

  paymentMiniInline: {
    display: "inline-flex",
    alignItems: "center",
    gap: 7,
    lineHeight: 1,
  },

  paymentLogoBox: {
    width: 34,
    height: 22,
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    borderRadius: 6,
    border: "1px solid #EEF0F2",
    overflow: "hidden",
    background: "#fff",
    color: "#111",
    flexShrink: 0,
  },

  paymentLogoBoxFailed: {
    border: "1px solid #FCA5A5",
  },

  paymentCardSvg: {
    width: 34,
    height: 22,
    objectFit: "contain",
  },

  paymentLast4: {
    fontSize: "0.78rem",
    fontWeight: 750,
    letterSpacing: "-0.01em",
  },

  paymentNoCard: {
    fontSize: "0.78rem",
    fontWeight: 700,
    color: "#777",
  },

  minimalFooterWrap: {
    width: "100vw",
    marginLeft: "calc(50% - 50vw)",
    marginRight: "calc(50% - 50vw)",
    alignSelf: "center",
    marginTop: "auto",
    paddingTop: "48px",
    boxSizing: "border-box",
  },
};